import '../product-card/product-card-display';
import store from '../../store/store';
import variables from '../../common/styles/variables';
import { BaseComponent } from '../base-component/base-component';
import { Catalog } from '../../common/constants/catalog';
import { ContentCardData } from './content-card-display';
import { PROFESSIONAL_SITE_URL } from '../../common/constants/professional-redirects';
import { ProductCardData } from '../product-card/product-card-display';
import { SearchResult, ViewTypeLabel } from './interfaces/search.interface';
import { StoreState } from '../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html, css, nothing } from 'lit';
import { isCustomISBN, isSuccess, selectRequest, selectResults, selectSelectedCatalog } from './store/search.selector';
import { isSearchPage, reverseCatalogMap } from '../../common/utils/util';
import { mapContentCardData } from './mapping/search-content-card.mapping';
import { mapProductCardData } from './mapping/search-product-card.mapping';
import { renderCustomISBNTemplate, renderNoResultTemplate, renderTabbedSearchNoResultTemplate } from './utils/search-results.util';
import { populateRelatedProductsMapper } from './utils/search.util';

/**
 * An element to display search results
 *
 * @csspart result - The individual search results.
 * @csspart large-card - The container of the large-card variation.
 * @csspart small-card - The container of the small-card variation.
 * @csspart image - Available in the small-card and large-card variations.
 * @csspart heading - Available in the large-card and small-card variations.
 * @csspart header - Available in the large-card variation.
 * @csspart details - Available in the large-card variation.
 * @csspart add-to-cart - Available in the large-card variation.
 * @csspart link - Available in the large-card variation.
 */
@customElement('mhecomm-search-results')
export class SearchResults extends connect(store)(BaseComponent) {
  static styles = [
    variables,
    css`
      :host {
        display: block;
      }
      #professional-site-banner {
        background-color: #d7e7f2 !important;
        border-radius: 0.5rem !important;
      }
    `,
  ];

  /**
   * The variation of the product card to display in different variations or styles, the values can be small-card, large-card.
   */
  @property()
  variation = 'small-card';

  /**
   * This property will decide whether search result need to show in List or Card view.
   * Currently this is being used in PLP search result page.
   */
  @property()
  view = '';

  @property()
  selectedTabName = "";

  @property()
  selectedTabValue = "";

  @state()
  private productCards: Array<ProductCardData> = [];

  @state()
  private contentCards: Array<ContentCardData> = [];

  @state()
  private catalog = 'unified';

  @state()
  private store?: string;

  @state()
  private results: Array<SearchResult> = [];

  @state()
  private isProductCards = false;

  @state()
  private isContentCards = false;

  @state()
  private selectedCatalog;

  @state()
  isCustomISBN?: boolean;

  /**
   * A flag to determine if the current page is the search page.
   */
  private readonly isSearchPage: boolean;

  constructor() {
    super();
    this.isSearchPage = isSearchPage();
  }

  renderComp() {
    return html`
      <div id="search-results" tabindex="-1" role="region" aria-label="Results">
        ${this.professionalSiteBanner()} ${this.productCardList()} ${this.contentCardList()}
        ${this.customIsbnOrNoResultTemplate()}
      </div>
    `;
  }

  productCardList() {
    return html`
      ${this.isProductCards && this.productCards?.length > 0
        ? html`
            <div class="row row-cols-1 ${this.view === 'card' ? 'row-cols-md-1 row-cols-lg-2 g-3' : ''}">
              ${this.view === ViewTypeLabel.LIST
                ? html`<div class="col">
                    <div class="row py-2 border-bottom d-none d-md-flex">
                      <div class="col-12 col-md-5 col-lg-7 fw-bold">Title</div>
                      <div class="col-12 col-md-7 col-lg-5 row">
                        <div class="col-3 fw-bold">Price</div>
                        <div class="col-9 fw-bold">Quantity</div>
                      </div>
                    </div>
                  </div>`
                : nothing}
              ${this.productCards.map((product, index) => html`
                  <mhecomm-product-card-display
                    exportparts="heading, container, image, header, details, add-to-cart, link"
                    variation=${this.variation}
                    .store=${this.store}
                    .product="${product}"
                    view=${this.view}
                    class="col"
                    ?isSearchResults=${true}
                    .productIndex="${index}"
                  >
                  </mhecomm-product-card-display>
                `
              )}
            </div>
          `
        : nothing}
    `;
  }

  contentCardList() {
    return html`
      ${this.isContentCards
        ? this.contentCards.map(
            (content, index) => html`
              ${this.selectedCatalog
                ? html`
                    <mhecomm-product-card-display
                      exportparts="heading, container, image, header, details, add-to-cart, link"
                      variation=${this.variation}
                      .store=${this.store}
                      .product="${content}"
                      view=${this.view}
                      class="col"
                      ?isSearchResults=${true}
                      .productIndex="${index}"
                    >
                    </mhecomm-product-card-display>
                  `
                : html`
                    <mhecomm-content-card-display
                      exportparts="heading, container, image, header, details, add-to-cart, link"
                      part="result"
                      variation=${this.variation}
                      .content="${content}"
                      .productIndex="${index}"
                    >
                    </mhecomm-content-card-display>
                  `}
            `
          )
        : ''}
    `;
  }

  private professionalSiteBanner() {
    return html`${isSearchPage() && this.selectedCatalog === Catalog.professional
      ? html`<div class="pb-3">
          <div id="professional-site-banner" class="p-3">
            <div>Most of our professional products can be found on our professional website.</div>
            <a
              href="${PROFESSIONAL_SITE_URL}"
              data-automation-id="go-to-professional-site"
              class="text-decoration-none"
              target="_blank"
            >
              <button class="btn btn-primary mt-3 mb-1">
                Go to Professional Site&nbsp;
                <uxt-icon icon="chevron-right"></uxt-icon>
              </button>
            </a>
          </div>
        </div>`
      : nothing}`;
  }

  private customIsbnOrNoResultTemplate() {
    return html` ${isSearchPage() && isSuccess(store.getState() as StoreState) && this.results.length === 0
      ? html`${this.isCustomISBN ? renderCustomISBNTemplate() : this.selectedTabName ? renderTabbedSearchNoResultTemplate(this.selectedTabName, this.selectedTabValue) : renderNoResultTemplate()}`
      : nothing}`;
  }

  stateChanged(state: StoreState) {
    const request = selectRequest(state);
    this.results = selectResults(state) || [];
    this.catalog = reverseCatalogMap(request ? request.catalog : '');
    this.store = request?.store;
    this.selectedCatalog = selectSelectedCatalog(state);
    this.isCustomISBN = isCustomISBN(state);

    this.isProductCards = ['seg', 'seg-store', 'he', 'seg-product'].includes(this.catalog);
    this.isContentCards = ['unified', 'content', 'articles', 'products'].includes(this.catalog);

    if (this.isProductCards) {
      this.productCards = mapProductCardData(
        this.results,
        this.catalog,
        this.selectedCatalog,
        this.store,
        this.isSearchPage
      );
      this.contentCards = [];
      this.saveRelatedProductsToLocalStorage(this.productCards);
    } else if (this.isContentCards) {
      this.productCards = [];
      this.contentCards = mapContentCardData(this.results, this.catalog, this.store, this.isSearchPage);
      this.saveRelatedProductsToLocalStorage(this.contentCards);
    } else {
      this.productCards = [];
      this.contentCards = [];
    }
  }

  scrollToResult() {
    const stickyTopElementHeight = document.querySelector('.sticky-top')?.clientHeight;
    if (stickyTopElementHeight) {
      window.scrollTo({
        top: stickyTopElementHeight,
        behavior: 'smooth',
      });
    } else {
      this.scrollTo();
    }
  }

  saveRelatedProductsToLocalStorage(products: Array<ProductCardData> | Array<ContentCardData>) {
    products.map((product) => {
      if (product.relatedProducts) {
        populateRelatedProductsMapper(product.relatedProducts, product?.id || product?.programSourceId);
        return;
      }
    })
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-results': SearchResults;
  }
}
